import * as React from "react";
import {
  BrowserRouter as Router, Routes, Route, useLocation,
} from "react-router-dom";
import { ProductContext } from "./context/ProductsContext";
import {
  initFacebookPixels,
  trackBrowserEvent,
  sendCAPIEvent,
  generateEventId,
  generateFBC,
  getFBC,
  getFBP,
  formatPhoneForFacebook,
} from "./pixel";
import { config } from "./config";

// ── Critical path: customer-facing, loaded eagerly ────────────────────────────
import LandingPage  from "./pages/Landing/LandingPage";
import ProductPage  from "./pages/ProductPage";
import ThankYou     from "./pages/ThankYou";

// ── Lazy-loaded: deferred until navigation hits the route ────────────────────
// Dashboard alone carries GrapesJS + antd + recharts + 10 GrapesJS plugins.
const Dashboard        = React.lazy(() => import("./pages/Dashboard/Dashboard"));
const Register         = React.lazy(() => import("./pages/Dashboard/Register"));
const Login            = React.lazy(() => import("./pages/Dashboard/Login"));
const LandingPageEditor= React.lazy(() => import("./pages/Dashboard/LandingPage/LandingPageEditor"));
const Shop             = React.lazy(() => import("./pages/Shop"));
const OrderPage        = React.lazy(() => import("./pages/OrderPage"));
const Contact          = React.lazy(() => import("./pages/Contact"));
const LegalPage        = React.lazy(() => import("./pages/LegalPage"));
const Checkout         = React.lazy(() => import("./pages/Checkout"));
const SingleCategory   = React.lazy(() => import("./pages/SingleCategory"));
const ThankYouPage     = React.lazy(() => import("./pages/ThankYou/Thankyou"));
const PrivateRoute     = React.lazy(() => import("./pages/Dashboard/PrivateRoute"));

// Paths where we skip PageView tracking (admin/auth pages).
const SKIP_PAGEVIEW_PATHS = ['/dashboard', '/login', '/register', '/editor'];

// ── Minimal fallback shown while lazy chunks load ────────────────────────────
const PageFallback = () => (
  <div style={{
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#f8fafc',
  }}>
    <div style={{
      width: 36, height: 36, border: '3px solid #e2e8f0',
      borderTopColor: '#10b981', borderRadius: '50%',
      animation: 'spin 0.7s linear infinite',
    }} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// ── SPA PageView Tracker ──────────────────────────────────────────────────────
const PageViewTracker = React.memo(() => {
  const location  = useLocation();
  const { pixel, testEventCode } = React.useContext(ProductContext);
  const isFirstRender = React.useRef(true);

  React.useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (!pixel?.length) return;
    if (typeof window === 'undefined' || !window.fbq) return;
    if (SKIP_PAGEVIEW_PATHS.some(p => location.pathname.startsWith(p))) return;

    const eventId = generateEventId('PV');
    trackBrowserEvent(pixel, 'PageView', { event_source_url: window.location.href }, eventId);
    sendCAPIEvent(config.apiUrl, 'PageView', {
      event_source_url: window.location.href, fbc: getFBC(), fbp: getFBP(),
    }, {}, eventId, testEventCode);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
});

// ── App ───────────────────────────────────────────────────────────────────────
function App() {
  const { pixel, testEventCode } = React.useContext(ProductContext);
  const pixelInitialized = React.useRef(false);

  React.useEffect(() => {
    if (!pixel?.length || pixelInitialized.current) return;
    generateFBC();
    const storedPhone = localStorage.getItem('phone') || '';
    const advancedMatch = storedPhone ? { ph: formatPhoneForFacebook(storedPhone) } : {};
    initFacebookPixels(pixel, advancedMatch);
    pixelInitialized.current = true;

    const eventId    = generateEventId('PV');
    const currentPath = window.location.pathname;
    if (!SKIP_PAGEVIEW_PATHS.some(p => currentPath.startsWith(p))) {
      trackBrowserEvent(pixel, 'PageView', { event_source_url: window.location.href }, eventId);
      sendCAPIEvent(config.apiUrl, 'PageView', {
        event_source_url: window.location.href, fbc: getFBC(), fbp: getFBP(),
      }, {}, eventId, testEventCode);
    }
  }, [pixel]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="App">
      <Router>
        <PageViewTracker />
        <React.Suspense fallback={<PageFallback />}>
          <Routes>
            {/* ── Customer-facing (eager, no Suspense overhead) ── */}
            <Route path="/"              element={<ProductPage />} />
            <Route path="/offer/:slug"   element={<LandingPage />} />
            <Route path="/lp"            element={<LandingPage />} />
            <Route path="/thankyou/:id"  element={<ThankYou />} />

            {/* ── Lazy-loaded routes ── */}
            <Route path="/login"         element={<Login />} />
            <Route path="/register"      element={<Register />} />
            <Route path="/shop"          element={<Shop />} />
            <Route path="/:slug"         element={<OrderPage />} />
            <Route path="/legal/:slug"   element={<LegalPage />} />
            <Route path="/category/:id"  element={<SingleCategory />} />
            <Route path="/contact"       element={<Contact />} />
            <Route path="/checkout"      element={<Checkout />} />
            <Route path="/th-1"          element={<ThankYouPage />} />
            <Route path="/editor/:id"    element={<LandingPageEditor />} />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute allowedRoles={["admin", "moderator"]}>
                  <Dashboard />
                </PrivateRoute>
              }
            />
          </Routes>
        </React.Suspense>
      </Router>
    </div>
  );
}

export default App;
